import { UVMapping } from "three";
import { CACHEITEMSTATE } from "../../engine/internal";
import { InfraScene } from "../scenes/internal";
import { isNull } from "../../internal";
import { QuadtreeUtils } from "../../utils/internal";
import { SceneGraphNode } from "../internal";
/**
 * Created by maurus.daehler on 18.01.2017.
 */
export var QuadtreeDisplayMode;
(function (QuadtreeDisplayMode) {
    QuadtreeDisplayMode[QuadtreeDisplayMode["PLAYINGFORWARD"] = 0] = "PLAYINGFORWARD";
    QuadtreeDisplayMode[QuadtreeDisplayMode["PLAYINGBACKWARD"] = 1] = "PLAYINGBACKWARD";
    QuadtreeDisplayMode[QuadtreeDisplayMode["FRAMESTEPPINGFORWARD"] = 2] = "FRAMESTEPPINGFORWARD";
    QuadtreeDisplayMode[QuadtreeDisplayMode["FRAMESTEPPINGBACKWARD"] = 3] = "FRAMESTEPPINGBACKWARD";
    QuadtreeDisplayMode[QuadtreeDisplayMode["FRAMESLIDINGFORWARD"] = 4] = "FRAMESLIDINGFORWARD";
    QuadtreeDisplayMode[QuadtreeDisplayMode["FRAMESLIDINGBACKWARD"] = 5] = "FRAMESLIDINGBACKWARD";
    QuadtreeDisplayMode[QuadtreeDisplayMode["DIRECT"] = 6] = "DIRECT";
})(QuadtreeDisplayMode || (QuadtreeDisplayMode = {}));
export class ImageQuadtree extends SceneGraphNode {
    /**
     * @method constructor
     * @param {Array.<string>} baseUrls
     * @param {number} tileWidth
     * @param {number} imageWidth
     * @param {number} imageHeight
     * @param {number} pxsize
     * @param {number} minZoom
     * @param {number} maxZoom
     * @param {number} cachingRange
     */
    constructor(baseUrls, tileWidth, imageWidth, imageHeight, pxsize, minZoom, maxZoom, cachingRange) {
        super(null);
        this.currentImageIndex = -1;
        this.hide = false;
        /**
         * Holds all visible tiles
         * @property {Tile[]} lstFrustumTiles
         * @default []
         */
        this.lstFrustumTiles = [];
        this.baseUrls = [];
        this.onZoom = null;
        this.onPan = null;
        /**
         * while caching if this tile is ready we can fire the cache ready event...
         * @type {string}
         */
        this.waitforurl = "";
        this.requestedImageIndex = 0;
        this.cachingStepSize = 1;
        this.frameskippedcbf = null;
        this._imageChanged = false;
        this._forceLod0prev = false;
        /**
         * Represents the amount of items that are currently being cached
         * @property {number} _cachingQueueSize
         * @privatge
         */
        this._cachingQueueSize = 0;
        this._cachingDepth = 0;
        /**
         * is true when the caching cicle is completed
         * @property {boolean} _cachingCompleted
         * @default true
         * @private
         */
        this._cachingCompleted = true;
        /**
         * image width in normalized quadtree coordinates
         * @property {number} imageWidth
         * @default 0
         */
        this.imageWidth = 0;
        /**
         * image height in normalized quadtree coordinates
         * @property {number} imageHeight
         * @default 0
         */
        this.imageHeight = 0;
        this.active = false;
        /**
         * Force quadtree to only show lod 0 (Required for video playback)
         * @property {boolean} _forceLod0
         * @default false
         */
        this._forceLod0 = false;
        this._iterator = { count: 0 };
        this._allTilesReady = false;
        this._allTilesReadyPrev = true;
        /**
         * {Function} visibleTilesNotReady fired if a currently visible tile is not ready for display (texture is not loaded)
         * @default null
         */
        this.visibleTilesNotReady = null;
        /**
         * {Function} visibleTilesReady only fired if visibleTilesNotReady was fired before and all textures are completely loaded now....
         * @default null
         */
        this.visibleTilesReady = null;
        this.cacheNotReadyEvent = null;
        this.cacheReadyEvent = null;
        this._cacheNotReadyEventSent = false;
        /**
         * The display Mode (Playing,Framestepping etc..) is determined on every setImageindex call and has influence on caching etc.
         * @type {QuadtreeDisplayMode}
         */
        this.displayMode = QuadtreeDisplayMode.FRAMESTEPPINGFORWARD;
        //Variables for determining display mode
        this.inputoverflow = false;
        this.lastcall = -1;
        this.iotimer = -1;
        this.iocbf = null;
        this.faceindex = 0;
        this.baseUrls = baseUrls;
        this.tileWidth = tileWidth;
        this.imageWidth = imageWidth;
        this.imageHeight = imageHeight;
        this.pixelsize = pxsize;
        this.minZoom = minZoom;
        this.maxZoom = maxZoom;
        this._cachingRange = cachingRange;
    }
    destroy() {
        this.wipeTiles();
        this.currentTraversalState.scene.engine.textureCache.clear();
    }
    wipeTiles() {
        return;
    }
    render(traversalState) {
        super.render(traversalState);
        traversalState.actualQuadtree = this;
        if (!(traversalState.scene instanceof InfraScene)) {
            throw "Scene Error: ImageQuadtree can only be attachted to type InfraScene";
        }
    }
    forceLod0(value) {
        this._forceLod0 = value;
    }
    changeToRequestedImage() {
        return;
    }
    setImageIndex(index, playmode = false, cachingstepsize) {
        return;
    }
    mouseToMap(mx, my, all) {
        return [0, 0];
    }
    readPixelValue(x, y, absolute) {
        return [0.0, 0.0, 0.0, 0.0];
    }
    /**
     * Determines the display mode according call-frequency and requested framenumbers...
     * @param framenumber
     * @param playmode
     */
    determineDisplayMode(index, playmode) {
        if (playmode && index >= this.currentImageIndex) {
            this.displayMode = QuadtreeDisplayMode.PLAYINGFORWARD;
        }
        if (playmode && index < this.currentImageIndex) {
            this.displayMode = QuadtreeDisplayMode.PLAYINGBACKWARD;
        }
        if (!playmode && index >= this.currentImageIndex) {
            this.displayMode = QuadtreeDisplayMode.FRAMESTEPPINGFORWARD;
        }
        if (!playmode && index < this.currentImageIndex) {
            this.displayMode = QuadtreeDisplayMode.FRAMESTEPPINGBACKWARD;
        }
        //determine if it's an inputoverflow
        this.iocbf = () => {
            this.inputoverflow = false;
            this.changeToRequestedImage();
            this.setImageIndex(this.currentImageIndex);
            this.currentTraversalState.scene.render();
        };
        const n = Date.now();
        const dt = n - this.lastcall;
        const thresh = 50;
        //normal render loop for ie: t= 15..30 for chrome and FF: t=2..4
        if (dt < thresh && !playmode) {
            //input overflow don't load images...
            this.inputoverflow = true;
            window.clearTimeout(this.iotimer);
            this.iotimer = window.setTimeout(() => {
                this.iocbf();
            }, thresh * 1.5);
        }
        this.lastcall = n;
        //determine if it's an inputoverflow
        if (!playmode && this.inputoverflow && index > this.currentImageIndex) {
            this.displayMode = QuadtreeDisplayMode.FRAMESLIDINGFORWARD;
        }
        if (!playmode && this.inputoverflow && index < this.currentImageIndex) {
            this.displayMode = QuadtreeDisplayMode.FRAMESLIDINGBACKWARD;
        }
    }
    _cacheSequence() {
        this._cacheImages(this.requestedImageIndex, this._cachingRange, 0, true, this.cachingStepSize);
    }
    _cacheImages(index, range, depth, frustumCulled, cachingStepSize) {
        this._cachingCompleted = false;
        const reverse = this.displayMode == QuadtreeDisplayMode.PLAYINGFORWARD ||
            this.displayMode == QuadtreeDisplayMode.FRAMESTEPPINGFORWARD
            ? false
            : true;
        if (index < 0)
            index = 0;
        const framelist = ImageQuadtree.clampRangeToFramelist(index, range, cachingStepSize, reverse, 0, this.baseUrls.length - 1);
        const urlsToLoad = framelist.map((x) => {
            return {
                cam: this.faceindex,
                quadKey: QuadtreeUtils.TileCoordToQuadKey(0, 0, 0),
                url: ImageQuadtree.getOverviewImageUrl(this.baseUrls[x], this.faceindex),
            };
        });
        if (this.currentImageIndex < this.baseUrls.length) {
            this.waitforurl = this.baseUrls[this.requestedImageIndex] + "/" + [this.faceindex] + "/?lod=0&x=0&y=0";
        }
        else {
            this.waitforurl = "";
        }
        this._cachingQueueSize = urlsToLoad.length;
        for (let it = urlsToLoad.length - 1; it >= 0; it--) {
            //check if url is not already in loading state
            const state = this.currentTraversalState.scene.engine.isInTextureCache(urlsToLoad[it].url);
            if (state == CACHEITEMSTATE.LOADING || state == CACHEITEMSTATE.LOADED) {
                if (state == CACHEITEMSTATE.LOADED)
                    this._cachingQueueSize -= 1;
                urlsToLoad.splice(it, 1);
            }
        }
        for (let it = 0; it < urlsToLoad.length; it++) {
            this.currentTraversalState.scene.engine.requestTexture(urlsToLoad[it]["url"], UVMapping, (a, url) => {
                this._cacheloadsuccess(a);
            }, (url) => {
                this._cacheloadfailed();
            }, false);
        }
        const state = this.currentTraversalState.scene.engine.isInTextureCache(this.waitforurl);
        if (state == CACHEITEMSTATE.LOADED)
            this._cacheloadcompleted();
    }
    _cacheloadsuccess(texture) {
        // SUCCESS CODES
        this._cachingQueueSize -= 1;
        if (texture.name.indexOf(this.waitforurl) > -1 || this.waitforurl == "") {
            //cache ready
            this._cacheloadcompleted();
            this.changeToRequestedImage();
            return;
        }
    }
    _cacheloadfailed() {
        this._cachingQueueSize -= 1;
        console.log("Help, I failed");
        this._cacheloadcompleted();
    }
    _cacheloadcompleted() {
        this._cachingCompleted = true;
        if (!isNull(this.cacheReadyEvent)) {
            this.cacheReadyEvent();
            this._cacheNotReadyEventSent = false;
            if (this.currentImageIndex != this.requestedImageIndex) {
                this._imageChanged = true;
                this.currentImageIndex = this.requestedImageIndex;
                this.currentTraversalState.scene.render();
            }
        }
    }
    static getOverviewImageUrl(baseUrl, sensorid) {
        return `${baseUrl}/${sensorid}/?lod=0&x=0&y=0`;
    }
    static clampRangeToFramelist(index, range, cachingStepSize, reverseMode, minindex, maxindex) {
        const framelist = [];
        //if only every second or third image is loaded -> extend caching range.
        range = range * cachingStepSize;
        if (!reverseMode) {
            const absRange = index + range >= maxindex ? maxindex : index + range;
            for (let i = index; i <= absRange; i += cachingStepSize) {
                if (i > maxindex)
                    i = maxindex;
                framelist.push(i);
            }
        }
        else {
            const absRange = index - range <= minindex ? minindex : index - range;
            for (let i = index; i >= absRange; i -= cachingStepSize) {
                if (i < minindex)
                    i = minindex;
                if (i > maxindex)
                    i = maxindex;
                framelist.push(i);
            }
        }
        return framelist;
    }
}
//# sourceMappingURL=imagequadtree.js.map